/**
 * Slash-command token engine. The editor's caret context (`text before the caret
 * within the current line`) drives `detectSlashToken`; selection routes through
 * `applySlashCommand`. Photo/audio are action commands: the caller opens a
 * picker/upload first, then re-applies the command with the committed attachment id.
 */

export type SlashCommandId = 'h1' | 'h2' | 'h3' | 'todo' | 'bench' | 'photo' | 'audio'

export interface SlashCommand {
  id: SlashCommandId
  label: string
  /** Monospace syntax hint shown to the right of the label in the menu. */
  hint: string
  description: string
  kind: 'block' | 'action'
  /** Text inserted for block commands. */
  insert?: string
  action?: 'open-photo' | 'open-audio'
}

export const SLASH_COMMANDS: readonly SlashCommand[] = [
  {
    id: 'h1',
    label: 'Heading 1',
    hint: '/h1',
    description: 'Section title',
    kind: 'block',
    insert: '# ',
  },
  {
    id: 'h2',
    label: 'Heading 2',
    hint: '/h2',
    description: 'Subsection title',
    kind: 'block',
    insert: '## ',
  },
  {
    id: 'h3',
    label: 'Heading 3',
    hint: '/h3',
    description: 'Field-label heading',
    kind: 'block',
    insert: '### ',
  },
  {
    id: 'todo',
    label: 'Todo',
    hint: '/todo',
    description: 'Checkbox task line',
    kind: 'block',
    insert: '- [ ] ',
  },
  {
    id: 'bench',
    label: 'Bench mark',
    hint: '/bench',
    description: 'Measurement with unit',
    kind: 'block',
    insert: '::bench label = value unit',
  },
  {
    id: 'photo',
    label: 'Photo',
    hint: '/photo',
    description: 'Upload and attach a photo',
    kind: 'action',
    action: 'open-photo',
  },
  {
    id: 'audio',
    label: 'Audio',
    hint: '/audio',
    description: 'Record and attach audio',
    kind: 'action',
    action: 'open-audio',
  },
]

export interface SlashToken {
  /** Partial trigger after the slash, e.g. 'ben'. Empty string right after '/'. */
  query: string
  /** Index of the '/' within the given line-before-caret string. */
  tokenStart: number
}

const TRIGGER_RE = /^[A-Za-z0-9-]*$/

/**
 * Detect an active slash token at the caret. `lineBeforeCaret` is the substring
 * of the current line from its start up to the caret position.
 */
export function detectSlashToken(lineBeforeCaret: string): SlashToken | null {
  const slash = lineBeforeCaret.lastIndexOf('/')
  if (slash === -1) return null
  const before = lineBeforeCaret[slash - 1]
  if (before !== undefined && !/\s/.test(before)) return null
  const query = lineBeforeCaret.slice(slash + 1)
  if (!TRIGGER_RE.test(query)) return null
  return { query, tokenStart: slash }
}

export function matchCommands(query: string): SlashCommand[] {
  const q = query.toLowerCase()
  return SLASH_COMMANDS.filter((command) => command.id.startsWith(q))
}

export interface SlashApplyResult {
  value: string
  caret: number
}

/**
 * Replace the active slash token (if any) with the command's insertion.
 * Returns null when the caret context is invalid, or when an action command is
 * applied without its attachment id yet — call `applySlashCommand` again once
 * the upload commits.
 */
export function applySlashCommand(
  input: string,
  caret: number,
  command: SlashCommand,
  attachmentId?: string,
): SlashApplyResult | null {
  const lineStart = input.lastIndexOf('\n', Math.max(0, caret - 1)) + 1
  const lineBefore = input.slice(lineStart, caret)
  const token = detectSlashToken(lineBefore)
  if (!token) return null

  let insert: string
  if (command.kind === 'action') {
    if (attachmentId === undefined) return null
    insert = command.action === 'open-photo' ? `!photo ${attachmentId} ` : `!audio ${attachmentId} `
  } else if (command.insert !== undefined) {
    insert = command.insert
  } else {
    return null
  }

  const tokenStartAbs = lineStart + token.tokenStart
  const value = input.slice(0, tokenStartAbs) + insert + input.slice(caret)
  return { value, caret: tokenStartAbs + insert.length }
}

/** True when the caret sits on an active slash token — used to render/keep the menu open. */
export function slashMenuActive(input: string, caret: number): SlashToken | null {
  const lineStart = input.lastIndexOf('\n', Math.max(0, caret - 1)) + 1
  return detectSlashToken(input.slice(lineStart, caret))
}
